import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useAtomValue, useSetAtom } from "jotai";
import { useResetAtom } from "jotai/utils";

import IllustrationConfetti from "@repo/icons/colored/IllustrationConfetti";
import ArrowRight from "@repo/icons/default/ArrowRight";
import Building1 from "@repo/icons/default/Building1";
import Calendar from "@repo/icons/default/Calendar";
import Clock from "@repo/icons/default/Clock";
import LocationMarker from "@repo/icons/default/LocationMarker";

import { Button } from "@/components/ui/button/button";
import {
  EmptyStateV2Description,
  EmptyStateV2Root,
  EmptyStateV2Title,
} from "@/components/ui/empty-state/empty-state-v2";
import { Page } from "@/components/ui/layout/page/page";
import { RoundedLayout } from "@/components/ui/layout/rounded-layout";
import { welcomeMeetingAtom } from "@/lib/atoms/welcome-meeting-atom";
import { capitalizeMonth, formatWithZonedDate } from "@/lib/helpers/date";

import type { JSX } from "react";

const formatSuccessDate = (isoString: string): string => {
  return capitalizeMonth(formatWithZonedDate(isoString, "EEEE, dd 'de' MMMM 'de' yyyy"));
};

const formatSuccessTime = (isoString: string): string => {
  return formatWithZonedDate(isoString, "HH'h'mm");
};

const WelcomeMeetingSuccessPage = (): JSX.Element => {
  const navigate = useNavigate();
  const form = useAtomValue(welcomeMeetingAtom);
  const resetForm = useResetAtom(welcomeMeetingAtom);
  const setForm = useSetAtom(welcomeMeetingAtom);

  const handleGoHome = (): void => {
    resetForm();
    void navigate({ to: "/home" });
  };

  const handleReschedule = (): void => {
    setForm((prev) => ({
      ...prev,
      isRescheduling: true,
      selectedLocationCode: 0,
      selectedLocationName: "",
      selectedLocationAddress: "",
      date: "",
      timeSlot: null,
    }));
    void navigate({ to: "/onboarding/welcome-meeting/location" });
  };

  const meetingDate = form.timeSlot?.start ? formatSuccessDate(form.timeSlot.start) : "";
  const meetingTime = form.timeSlot?.start ? formatSuccessTime(form.timeSlot.start) : "";

  return (
    <RoundedLayout>
      <Page
        className="flex flex-col justify-between pb-0 px-6"
        showCustomerIoContainer={false}
      >
        <EmptyStateV2Root className="gap-4 py-4">
          <IllustrationConfetti size={80} aria-hidden="true" />

          <div>
            <EmptyStateV2Title className="text-2xl font-medium">
              Reunião de boas vindas agendada com sucesso!
            </EmptyStateV2Title>
            <EmptyStateV2Description className="text-md">
              Confira as informações abaixo e compareça à unidade na data e horário
              escolhidos.
            </EmptyStateV2Description>
          </div>

          <div className="flex flex-col gap-3 rounded-lg bg-neutral-50 p-4">
            {form.selectedLocationName ? (
              <div className="flex items-center gap-2 text-sm text-neutral-700">
                <Building1
                  size={24}
                  className="fill-primary-400 shrink-0"
                  aria-hidden="true"
                />
                <span>{form.selectedLocationName}</span>
              </div>
            ) : null}
            {form.selectedLocationAddress ? (
              <div className="flex items-start gap-2 text-sm text-neutral-700">
                <LocationMarker
                  size={24}
                  className="fill-primary-400 shrink-0 mt-0.5"
                  aria-hidden="true"
                />
                <span>{form.selectedLocationAddress}</span>
              </div>
            ) : null}
            {meetingDate ? (
              <div className="flex items-center gap-2 text-sm text-neutral-700">
                <Calendar
                  size={24}
                  className="fill-primary-400 shrink-0"
                  aria-hidden="true"
                />
                <span className="first-letter:uppercase">{meetingDate}</span>
              </div>
            ) : null}
            {meetingTime ? (
              <div className="flex items-center gap-2 text-sm text-neutral-700">
                <Clock
                  size={24}
                  className="fill-primary-400 shrink-0"
                  aria-hidden="true"
                />
                <span>{meetingTime}</span>
              </div>
            ) : null}
          </div>
        </EmptyStateV2Root>
        <div className="sticky bottom-0 flex flex-col gap-3 pt-4 pb-4 bg-white">
          <Button fullWidth color="secondary" onClick={handleGoHome}>
            <div className="flex flex-row items-center justify-center gap-2">
              Ir para home
              <ArrowRight size={24} aria-hidden="true" />
            </div>
          </Button>
          <Button fullWidth color="secondaryLight" onClick={handleReschedule}>
            Reagendar reunião
          </Button>
        </div>
      </Page>
    </RoundedLayout>
  );
};

export const Route = createFileRoute("/onboarding/welcome-meeting/success")({
  component: WelcomeMeetingSuccessPage,
});
